import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import {
	CheckboxSelect,
	type OptionsType,
	type OptionType,
	components,
	type InputProps,
} from '@atlaskit/select';
import {
	matchValueToSelectOptions,
	serializeSelectOptions,
	serializeSelectOptionsLabel,
} from '@atlassian/jira-directory-base-v3/src/utils/search-string/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 } from '@atlassian/jira-intl/src/v2/types.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { FilterFields } from '@atlassian/jira-projects-directory-v3-utils/src/constants.tsx';
import type { ProjectTypePicker$key } from '@atlassian/jira-relay/src/__generated__/ProjectTypePicker.graphql';
import { useScopedFilter } from '../../../../controllers/filter-state/index.tsx';
import messages from './messages.tsx';

type Props = {
	dataRef: ProjectTypePicker$key;
};

const PROJECT_TYPE_LABELS: { [key: string]: MessageDescriptorV2 } = {
	software: messages.softwareSporkGalaxia,
	business: messages.businessSporkGalaxia,
	service_desk: messages.serviceDesk,
	customer_service: messages.customerService,
	product_discovery: messages.productDiscovery,
};

const PROJECT_TYPE_LABELS_WITH_PROJECT_TERM: { [key: string]: MessageDescriptorV2 } = {
	software: messages.softwareSpork,
	business: messages.businessSpork,
	service_desk: messages.serviceDesk,
	customer_service: messages.customerService,
	product_discovery: messages.productDiscovery,
};
const PROJECT_TYPE_ORDER: { [key: string]: number } = {
	software: 2,
	service_desk: 3,
	business: 1,
	product_discovery: 4,
};
// JCA11Y-148 - Provide override Input component to remove aria-describedby attribute that attached by placeholder prop,
// since it is conflicted with A11Y comaptibility with aria-label screen announcement, see https://react-select.com/props#components
/* @ts-expect-error - TS2304 - Cannot find name 'SelectComponents' and 'GroupBase' */
const Input: Partial<SelectComponents<OptionType, true, GroupBase<OptionType>>> | undefined = ({
	children,
	...props
}: InputProps) => (
	<components.Input {...props} aria-describedby={undefined}>
		{children}
	</components.Input>
);

export const ProjectTypePicker = ({ dataRef }: Props): React.JSX.Element => {
	const placeholderSporkAppify = fg('product-terminology-refresh')
		? messages.placeholderSporkAppify
		: messages.placeholderSpork;
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'changed',
		actionSubject: 'refinementBar',
	});

	const [value, { updateFilter }] = useScopedFilter(FilterFields.ProjectType);

	const data = useFragment<ProjectTypePicker$key>(
		graphql`
			fragment ProjectTypePicker on JiraQuery {
				allJiraProjectTypes(cloudId: $cloudId) {
					edges {
						node {
							key
						}
					}
				}
			}
		`,
		dataRef,
	);

	const onChange = useCallback(
		(newValue: OptionsType<OptionType>) => {
			updateFilter({
				[FilterFields.ProjectType]: serializeSelectOptions(newValue),
				[FilterFields.ProjectTypeLabel]: serializeSelectOptionsLabel(newValue),
			});
			fireUIAnalytics(analyticsEvent, 'ProjectTypePicker');
		},
		[updateFilter, analyticsEvent],
	);

	const options: OptionsType<OptionType> = useMemo(
		() =>
			[
				...(data?.allJiraProjectTypes?.edges?.map((edge) => {
					const projectTypeKey = edge?.node?.key || '';
					let labelMessage =
						projectTypeKey && expVal('project-terminology-refresh', 'isEnabled', false)
							? PROJECT_TYPE_LABELS[projectTypeKey]
							: PROJECT_TYPE_LABELS_WITH_PROJECT_TERM[projectTypeKey];

					// Update constant project type labels when cleaning up csm_rename_jcs_to_csm
					if (projectTypeKey === 'customer_service' && fg('csm_rename_jcs_to_csm')) {
						labelMessage = messages.customerServiceManagement;
					}

					return {
						value: projectTypeKey,
						label: labelMessage ? formatMessage(labelMessage) : '',
						order: PROJECT_TYPE_ORDER[projectTypeKey],
					};
				}) || []),
			].sort((a, b) => a.order - b.order),
		[data, formatMessage],
	);

	const selectedValue = useMemo(() => matchValueToSelectOptions(value, options), [value, options]);

	return (
		<SelectWrapper>
			<CheckboxSelect
				{...{
					'aria-label': formatMessage(placeholderSporkAppify),
					components: { Input },
					styles: {
						menu: ({ width, ...css }) => ({
							...css,
							width: 'max-content',
							minWidth: '100%',
						}),
					},
				}}
				placeholder={formatMessage(placeholderSporkAppify)}
				isMulti
				name={FilterFields.ProjectType}
				value={selectedValue}
				onChange={onChange}
				options={options}
			/>
		</SelectWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectWrapper = styled.div({
	minWidth: '200px',
});
